import { isBefore } from "date-fns";
import { usePostHog } from "posthog-js/react";
import type React from "react";

import type { SurveyDetailsV2Dto } from "@/api/types";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { Tiles } from "@/design-system/Tiles";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useConfig } from "@/providers/config/useConfig";
import { surveyMutations } from "@/queries/surveys/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { SurveyFollowUpTipsModal } from "./SurveyFollowUpTipsModal";
import { SurveyFollowUpTipsModal_LEGACY } from "./SurveyFollowUpTipsModal_LEGACY";

enum SurveyFollowUpAction {
  CREATE_EVENT = "create_event",
  CREATE_MESSAGE = "create_message",
  CREATE_SURVEY = "create_survey",
  SEE_TIPS = "see_tips",
  IGNORE = "ignore",
}

interface SurveyActionSectionProps {
  survey: SurveyDetailsV2Dto;
}

export function SurveyActionSection({ survey }: SurveyActionSectionProps): React.ReactNode {
  const [isFollowUpModalOpened, followUpModalHandler] = useBool();

  const hasPermission = usePermission();
  const surveyAiFollowUpActions = useConfig("surveyAiFollowUpSuggestions");
  const { t } = useTranslation();
  const slug = useSlug();
  const addFollowUpDateMutation = surveyMutations.useAddFollowUpDate();
  const postHog = usePostHog();

  const handleClickFollowUpAction = (action: SurveyFollowUpAction) => {
    if (!survey.followedUpAt) {
      void addFollowUpDateMutation.mutateAsync(survey.id);
    }

    postHog.capture("clicked_survey_follow_up_action", {
      surveyId: survey.id,
      action,
      is_first_time: !survey.followedUpAt,
    });
  };

  const handleOpenTips = () => {
    followUpModalHandler.setTrue();

    handleClickFollowUpAction(SurveyFollowUpAction.SEE_TIPS);
  };

  const hasAiResults =
    surveyAiFollowUpActions &&
    survey.endsAt &&
    isBefore("2025-01-01T00:00:00.000Z", survey.endsAt) &&
    survey.canViewResults;

  return (
    <>
      <SectionCard
        title={t("page.survey-details.follow-up-actions.title")}
        description={t("page.survey-details.follow-up-actions.description")}
        renderBadges={
          survey.followedUpAt
            ? () => (
                <Badge icon={<CheckIcon />} variant="success">
                  {t("page.survey-details.follow-up-actions.label.followed-up")}
                </Badge>
              )
            : undefined
        }
      >
        <Stack gap="sm">
          <Tiles columns={{ base: 1, container: { sm: 2 } }} gap="sm">
            {hasAiResults ? (
              <Button variant="ai" fullWidth onClick={handleOpenTips}>
                {t("page.survey-details.follow-up-actions.button.see-tips")}
              </Button>
            ) : (
              <Button variant="soft" fullWidth onClick={handleOpenTips}>
                {t("page.survey-details.follow-up-actions.button.see-tips")}
              </Button>
            )}
            {hasPermission((x) => x.events.canManage) && (
              <Button
                fullWidth
                variant="soft"
                type="link"
                onClick={() => handleClickFollowUpAction(SurveyFollowUpAction.CREATE_EVENT)}
                href={routes.events.create({ slug })}
              >
                {t("page.survey-details.follow-up-actions.button.create-event")}
              </Button>
            )}
            {hasPermission((x) => x.canCreatePosts) && (
              <Button
                fullWidth
                variant="soft"
                type="link"
                onClick={() => handleClickFollowUpAction(SurveyFollowUpAction.CREATE_MESSAGE)}
                href={routes.communityFeed.adminCreatePost({ slug })}
              >
                {t("page.survey-details.follow-up-actions.button.create-message")}
              </Button>
            )}
            {hasPermission((x) => x.surveys.canManage) && (
              <Button
                fullWidth
                variant="soft"
                type="link"
                onClick={() => handleClickFollowUpAction(SurveyFollowUpAction.CREATE_SURVEY)}
                href={routes.surveys.new({ slug })}
              >
                {t("page.survey-details.follow-up-actions.button.create-survey")}
              </Button>
            )}
          </Tiles>

          {!survey.followedUpAt && (
            <Button fullWidth variant="soft" onClick={() => handleClickFollowUpAction(SurveyFollowUpAction.IGNORE)}>
              {t("common.action.ignore")}
            </Button>
          )}
        </Stack>
      </SectionCard>

      {hasAiResults && (
        <SurveyFollowUpTipsModal
          surveyId={survey.id}
          isOpened={isFollowUpModalOpened}
          onOpenChange={followUpModalHandler.set}
        />
      )}
      {!hasAiResults && (
        <SurveyFollowUpTipsModal_LEGACY
          surveyId={survey.id}
          isOpened={isFollowUpModalOpened}
          onOpenChange={followUpModalHandler.set}
        />
      )}
    </>
  );
}
